import { PriceType } from '@/api/hooks/useTenantConfig'
import {
  MRDCell,
  MRDMultiSelectEditor,
  type MappingStatusFilter,
  PriceTypesEditor,
  RefDataSourceIdsEditor,
  suppressDropdownKeyboardEvent,
} from '@/components/Grid'
import { PriceTypesCell } from '@/components/Grid/CellRenderers/PriceTypesCell'
import { RefDataSourceIdsCell } from '@/components/Grid/CellRenderers/RefDataSourceIdsCell'
import { HistoryButton, HistoryDrawer } from '@/components/History'
import { useHasScope } from '@/components/RequireScope'
import { useMappingContext } from '@/contexts/MappingContext'
import { useObject } from '@/contexts/ObjectContext'
import { useTenantStore } from '@/stores/tenantStore'
import { brandedColumnDef } from '@/utils/branded'
import { WarningFilled } from '@ant-design/icons'
import {
  useCrossroadsMapping,
  useCrossroadsMappingHistory,
  useRestoreCrossroadsMapping,
} from '@api/hooks/useCrossroadsMappings'
import { fetchClient } from '@api/typedClient'
import {
  CrossroadsMapping,
  CrossroadsMappingType,
  MAPPING_ENTITY_MAP,
} from '@api/types'
import { GraviGrid } from '@gravitate-js/excalibrr'
import { MappingGridControls } from '@pages/Dashboard/panels/ReferenceDataPanel/components/MappingGridControls'
import { useDebouncedSearch } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { useMappingGridActions } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/useMappingGridActions'
import { usePaginatedGridDatasource } from '@pages/Dashboard/panels/ReferenceDataPanel/hooks/usePaginatedGridDatasource'
import {
  ColDef,
  ColumnApi,
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
  NewValueParams,
  RowClickedEvent,
  SetFilterValuesFuncParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { Input, Select, Tooltip } from 'antd'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useChildMappingGridParams } from '../hooks/useChildMappingGridParams'

// The counterparty linked_* fields filter a supplier fan-out during translation, so
// only a mapping flagged is_supplier consults them. They stay editable regardless,
// since is_supplier can be turned on afterwards.
const LINKED_SOURCE_ID_COLUMNS: Record<
  string,
  { field: string; mappingType: CrossroadsMappingType; title: string }
> = {
  'details.linked_terminal_source_ids': {
    field: 'linked_terminal_source_ids',
    mappingType: 'terminal',
    title: 'Linked Terminals',
  },
  'details.linked_product_source_ids': {
    field: 'linked_product_source_ids',
    mappingType: 'product',
    title: 'Linked Products',
  },
}

interface ReferenceDataGridProps {
  mappingType: CrossroadsMappingType
}

export function CrossroadsMappingGrid(props: ReferenceDataGridProps) {
  const { selectedTenant } = useTenantStore()
  let gridApiRef = useRef<GridApi>({} as GridApi)
  let columnApiRef = useRef<ColumnApi>({} as ColumnApi)
  const [params] = useSearchParams()
  const prefilteredSourceIds = useMemo(
    () => params.get('source_ids')?.split(',') ?? [],
    [params],
  )

  const { selectedMapping, setSelectedMapping, draggedEntity } =
    useMappingContext()

  const { selectedObject } = useObject()
  const { searchTerm, setSearchTerm } = useDebouncedSearch()
  const [statusFilters, setStatusFilters] = useState<MappingStatusFilter[]>([
    'mapped',
    'needs mapping',
  ])
  const [historyMapping, setHistoryMapping] =
    useState<CrossroadsMapping | null>(null)

  const historyMappingId = historyMapping?._id
  // isFetching, not isLoading: a reopen refetches over cached data, and the
  // drawer should show that rather than the previous result.
  const { data: mappingHistory, isFetching: isHistoryFetching } =
    useCrossroadsMappingHistory(historyMappingId)
  // The grid row is a projection of the stored document; the history diff needs
  // the document itself to describe the newest revision.
  const { data: storedMapping, isFetching: isStoredMappingFetching } =
    useCrossroadsMapping(historyMappingId)
  const restoreMutation = useRestoreCrossroadsMapping()
  // /crossroads/mappings/restore is gated on crossroads_mappings.edit, so only
  // offer it to users the backend would accept.
  const canEditMappings = useHasScope('crossroads_mappings.edit')
  const [restoringAuditId, setRestoringAuditId] = useState<string | null>(null)

  const {
    datasource,
    refresh,
    columnDefs: apiColumnDefs,
    totalRows,
  } = usePaginatedGridDatasource<CrossroadsMapping>({
    endpoint: '/crossroads/mappings/grid',
    mappingType: props.mappingType,
    searchTerm: searchTerm,
    mappingStatusFilters:
      statusFilters.length > 0 && statusFilters.length < 3
        ? statusFilters
        : undefined,
    sourceIds:
      prefilteredSourceIds.length > 0 ? prefilteredSourceIds : undefined,
  })

  const { handleMappingUpdate, handleSync } = useMappingGridActions({
    selectedObject: props.mappingType,
    refetch: refresh,
  })

  const detailGridRendererParams = useChildMappingGridParams()

  // The drawer closes on success: historyMapping is a snapshot of the row taken
  // at click time, and the grid needs a reload to show the restored values.
  const handleRestore = (auditId: string) => {
    if (!historyMappingId) return
    setRestoringAuditId(auditId)
    restoreMutation.mutate(
      {
        params: {
          query: { mapping_id: historyMappingId, audit_id: auditId },
        },
      },
      {
        onSuccess: () => {
          setHistoryMapping(null)
          refresh()
        },
        onSettled: () => setRestoringAuditId(null),
      },
    )
  }

  // Create a ref for the dragged entity to dodge the stale closure issue.
  const draggedEntityRef = useRef(draggedEntity)
  useEffect(() => {
    draggedEntityRef.current = draggedEntity
  }, [draggedEntity])

  // Determine if the goids column supports multi-select from the backend column_def
  const isMulti = useMemo(() => {
    const apiGoidsCol = apiColumnDefs.find((col) => col.field === 'goids')
    return (apiGoidsCol as Record<string, unknown>)?.multi !== false
  }, [apiColumnDefs])

  // Debug: track what's changing on re-renders
  const prevDeps = useRef<Record<string, unknown>>({})
  useEffect(() => {
    const deps: Record<string, unknown> = {
      apiColumnDefs,
      isMulti,
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      onCellValueChanged,
      onNestedCellValueChanged,
      datasource,
      selectedMapping,
      draggedEntity,
      selectedObject,
      searchTerm,
      statusFilters,
      detailGridRendererParams,
      onRowClicked,
    }
    const changed: string[] = []
    for (const [key, val] of Object.entries(deps)) {
      if (prevDeps.current[key] !== val) changed.push(key)
    }
    if (changed.length > 0) {
      console.log('[MappingGrid] changed deps:', changed)
    }
    prevDeps.current = deps
  })

  // Callbacks

  const getRowId = useCallback((params: GetRowIdParams<CrossroadsMapping>) => {
    return params.data!._id!
  }, [])

  const selectedMappingRef = useRef(selectedMapping)
  useEffect(() => {
    selectedMappingRef.current = selectedMapping
  }, [selectedMapping])

  const onRowClicked = useCallback(
    (event: RowClickedEvent<CrossroadsMapping>) => {
      if (
        selectedMappingRef.current &&
        selectedMappingRef.current._id == event.data!._id
      ) {
        setSelectedMapping(null)
        event.node.setSelected(false)
      } else {
        setSelectedMapping(event.data!)
        event.node.setSelected(true)
      }
    },
    [setSelectedMapping],
  )

  const onDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget!.classList.add('drag-over')
  }, [])

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget === e.target) {
      e.currentTarget.classList.remove('drag-over')
    }
  }, [])

  const onDrop = useCallback(
    async (
      e: React.DragEvent<HTMLDivElement>,
      params: ICellRendererParams<CrossroadsMapping>,
    ) => {
      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.classList.remove('drag-over')

      try {
        const goid = draggedEntityRef.current!.goid
        const existingGoids = params.data!.goids
        const api = params.api

        if (!existingGoids.includes(goid) && api) {
          const newGoids = isMulti ? [...existingGoids, goid] : [goid]
          const newMapping = { ...params.data! }
          newMapping.goids = newGoids
          await handleMappingUpdate(newMapping, params.data!, params.node)
        }
      } catch (error) {
        console.error('Error handling drop:', error)
      }
    },
    [draggedEntity, isMulti],
  )

  const onCellValueChanged = useCallback(
    async (
      params: NewValueParams<CrossroadsMapping, Record<string, unknown>>,
    ) => {
      const newMapping = params.data
      const field = params.colDef.field!.toString()
      const oldMapping = { ...newMapping, [field]: params.oldValue }
      await handleMappingUpdate(newMapping, oldMapping, params.node!)
    },
    [handleMappingUpdate],
  )

  const onNestedCellValueChanged = useCallback(
    async (
      params: NewValueParams<CrossroadsMapping, Record<string, unknown>>,
    ) => {
      const newMapping = params.data
      // For nested fields like "details.alternate_product_source_ids",
      // reconstruct the old mapping by restoring the nested value
      const field = params.colDef.field!.toString()
      const parts = field.split('.')
      // biome-ignore lint/suspicious/noExplicitAny: dynamic nested field restoration
      const oldMapping = { ...newMapping } as Record<string, any>
      const parent = parts[0]
      const child = parts[1]
      if (parts.length === 2 && parent && child) {
        oldMapping[parent] = {
          ...oldMapping[parent],
          [child]: params.oldValue,
        }
      } else {
        oldMapping[field] = params.oldValue
      }
      await handleMappingUpdate(
        newMapping,
        oldMapping as CrossroadsMapping,
        params.node!,
      )
    },
    [handleMappingUpdate],
  )

  // Columns - merge API columns with special columns
  const columnDefs = useMemo<ColDef<CrossroadsMapping>[]>(() => {
    const historyColumn: ColDef<CrossroadsMapping> = {
      headerName: '',
      colId: 'history',
      pinned: 'right',
      width: 56,
      initialFlex: 0,
      minWidth: 56,
      maxWidth: 56,
      sortable: false,
      filter: false,
      resizable: false,
      editable: false,
      suppressColumnsToolPanel: true,
      cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
        const mapping = params.data
        if (!mapping?._id) return null
        return <HistoryButton onClick={() => setHistoryMapping(mapping)} />
      },
    }

    // Special column for goids with custom renderer/editor
    const goidsColumn: ColDef<CrossroadsMapping> = {
      field: 'goids',
      headerName: 'Mapped To',
      editable: true,
      suppressKeyboardEvent: suppressDropdownKeyboardEvent,
      width: 350,
      maxWidth: 400,
      minWidth: 200,
      sort: 'asc',
      cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
        const { data } = params
        if (!data) return null
        return (
          <div
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, params)}
          >
            <MRDCell
              status={data.status}
              goids={data.goids || []}
              objectType={data.type || ''}
              maxVisible={1}
            />
          </div>
        )
      },
      cellEditor: MRDMultiSelectEditor,
      cellEditorPopup: true,
      cellEditorParams: {
        type: MAPPING_ENTITY_MAP[props.mappingType],
        multi: isMulti,
      },
      valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
        if (isMulti) {
          params.data.goids = params.newValue
        } else {
          params.data.goids = params.newValue != null ? [params.newValue] : []
        }
        return true
      },
      onCellValueChanged: onCellValueChanged,
    }

    // If we have API columns, use them and add our special columns
    if (apiColumnDefs.length > 0) {
      // Filter out goids from API columns (we have our own special version)
      const filteredApiCols = apiColumnDefs
        .filter((col) => col.field !== 'goids')
        .map((col) => {
          // Make the first column (usually display_name) use the group cell renderer
          if (col.field === 'display_name') {
            return {
              ...col,
              cellRenderer: 'agGroupCellRenderer',
              cellRendererParams: {
                innerRenderer: (
                  params: ICellRendererParams<CrossroadsMapping>,
                ) => {
                  const notInSync =
                    params.data?.found_in_latest_sync === false &&
                    params.data?.source_system === 'Gravitate Supply & Dispatch'
                  return (
                    <span>
                      {params.value}
                      {notInSync && (
                        <Tooltip
                          title={`This reference data with this source ID was not found in the most recent sync. It may not exist in ${params.data?.source_system}`}
                        >
                          <WarningFilled
                            style={{
                              color: 'var(--theme-warning)',
                              marginLeft: 6,
                              fontSize: 14,
                            }}
                          />
                        </Tooltip>
                      )}
                    </span>
                  )
                },
              },
              width: 310,
            }
          }
          return col
        })

      // Augment details.alternate_product_source_ids with custom editor/renderer
      const augmentCol = (
        col: ColDef<CrossroadsMapping>,
      ): ColDef<CrossroadsMapping> => {
        if ((col.field as string) === 'details.alternate_product_source_ids') {
          return {
            ...col,
            colId: 'details.alternate_product_source_ids',
            editable: true,
            suppressKeyboardEvent: suppressDropdownKeyboardEvent,
            cellEditor: RefDataSourceIdsEditor,
            cellEditorPopup: true,
            cellEditorParams: (
              params: ICellRendererParams<CrossroadsMapping>,
            ) => ({
              sourceSystem: params.data?.source_system,
              mappingType: 'product' as CrossroadsMappingType,
              placeholder: 'Select alternate products',
            }),
            cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
              const { data } = params
              if (!data) return null
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const details = (data as any).details
              const sourceIds = details?.alternate_product_source_ids ?? []
              return (
                <RefDataSourceIdsCell
                  sourceIds={sourceIds}
                  sourceSystem={data.source_system}
                  mappingType='product'
                  popoverTitle='Alternate Products'
                />
              )
            },
            valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const data = params.data as any
              if (!data.details) return false
              data.details.alternate_product_source_ids = params.newValue ?? []
              return true
            },
            onCellValueChanged: onNestedCellValueChanged,
          } as ColDef<CrossroadsMapping>
        }
        const linkedSourceIdCol = LINKED_SOURCE_ID_COLUMNS[col.field as string]
        if (linkedSourceIdCol) {
          const { field, mappingType, title } = linkedSourceIdCol
          return {
            ...col,
            colId: col.field as string,
            editable: true,
            suppressKeyboardEvent: suppressDropdownKeyboardEvent,
            cellEditor: RefDataSourceIdsEditor,
            cellEditorPopup: true,
            cellEditorParams: (
              params: ICellRendererParams<CrossroadsMapping>,
            ) => ({
              sourceSystem: params.data?.source_system,
              mappingType,
              placeholder: `Select ${title.toLowerCase()}`,
            }),
            cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
              const { data } = params
              if (!data) return null
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const details = (data as any).details
              return (
                <RefDataSourceIdsCell
                  sourceIds={details?.[field] ?? []}
                  sourceSystem={data.source_system}
                  mappingType={mappingType}
                  popoverTitle={title}
                />
              )
            },
            valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const data = params.data as any
              if (!data.details) return false
              data.details[field] = params.newValue ?? []
              return true
            },
            onCellValueChanged: onNestedCellValueChanged,
          } as ColDef<CrossroadsMapping>
        }
        if ((col.field as string) === 'details.linked_price_types') {
          return {
            ...col,
            colId: 'details.linked_price_types',
            editable: true,
            suppressKeyboardEvent: suppressDropdownKeyboardEvent,
            cellEditor: PriceTypesEditor,
            cellEditorPopup: true,
            cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
              const { data } = params
              if (!data) return null
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const details = (data as any).details
              return (
                <PriceTypesCell
                  priceTypes={details?.linked_price_types ?? []}
                />
              )
            },
            valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const data = params.data as any
              if (!data.details) return false
              data.details.linked_price_types = params.newValue ?? []
              return true
            },
            onCellValueChanged: onNestedCellValueChanged,
          } as ColDef<CrossroadsMapping>
        }
        if ((col.field as string) === 'source_id') {
          return {
            ...col,
            hide: false,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params: SetFilterValuesFuncParams) => {
                fetchClient
                  .GET(
                    // @ts-expect-error Dynamic endpoint not in OpenAPI spec
                    '/crossroads/mappings/source_ids',
                    {
                      params: {
                        query: { type: props.mappingType },
                      },
                    },
                  )
                  .then(({ data }) => {
                    params.success((data as string[]) ?? [])
                  })
              },
            },
          }
        }
        if ((col.field as string) === 'details.branded') {
          return {
            ...col,
            ...brandedColumnDef,
            editable: true,
            valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const data = params.data as any
              if (!data.details) return false
              data.details.branded = params.newValue ?? null
              return true
            },
            onCellValueChanged: onNestedCellValueChanged,
          } as ColDef<CrossroadsMapping>
        }
        if ((col.field as string) === 'details.default_price_type') {
          const priceTypeValues = Object.values(PriceType)
          return {
            ...col,
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
              values: [null, ...priceTypeValues],
            },
            valueFormatter: (params: { value: unknown }) =>
              params.value
                ? _.startCase(params.value as string)
                : '-- no default --',
            cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const details = (params.data as any)?.details
              const value = details?.default_price_type
              return value ? _.startCase(value) : ''
            },
            valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const data = params.data as any
              if (!data.details) return false
              data.details.default_price_type = params.newValue ?? null
              return true
            },
            onCellValueChanged: onNestedCellValueChanged,
          } as ColDef<CrossroadsMapping>
        }
        if ((col.field as string) === 'details.tenant_goid') {
          return {
            ...col,
            editable: true,
            cellEditor: MRDMultiSelectEditor,
            cellEditorParams: { type: 'Tenant', multi: false },
            suppressKeyboardEvent: suppressDropdownKeyboardEvent,
            cellRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const details = (params.data as any)?.details
              const goid = details?.tenant_goid
              return <MRDCell goids={goid ? [goid] : []} />
            },
            valueSetter: (params: ValueSetterParams<CrossroadsMapping>) => {
              // biome-ignore lint/suspicious/noExplicitAny: dynamic details field from API column defs
              const data = params.data as any
              if (!data.details) return false
              data.details.tenant_goid = params.newValue ?? null
              return true
            },
            onCellValueChanged: onNestedCellValueChanged,
          } as ColDef<CrossroadsMapping>
        }
        return col
      }

      // biome-ignore lint/suspicious/noExplicitAny: API column defs may contain grouped columns with children
      const processedCols = filteredApiCols.map((col: any) => {
        if (col.children) {
          return {
            ...col,
            children: (col.children as ColDef<CrossroadsMapping>[]).map(
              augmentCol,
            ),
          }
        }
        return augmentCol(col)
      }) as ColDef<CrossroadsMapping>[]

      // Insert goidsColumn as the second column (after display_name)
      const [firstCol, ...restCols] = processedCols
      return [firstCol, goidsColumn, ...restCols, historyColumn]
    }

    // Fallback to hardcoded columns if API hasn't returned yet
    return [
      {
        headerName: 'Name',
        field: 'display_name',
        width: 310,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          innerRenderer: (params: ICellRendererParams<CrossroadsMapping>) => {
            const notInSync =
              params.data?.found_in_latest_sync === false &&
              params.data?.source_system === 'Gravitate Supply & Dispatch'
            return (
              <span>
                {params.value}
                {notInSync && (
                  <Tooltip
                    title={`The reference data with this source ID was not found in the last sync. This reference data may no longer exist in ${params.data?.source_system}`}
                  >
                    <WarningFilled
                      style={{
                        color: 'var(--theme-warning)',
                        marginLeft: 6,
                        fontSize: 14,
                      }}
                    />
                  </Tooltip>
                )}
              </span>
            )
          },
        },
      },
      goidsColumn,
      {
        headerName: 'Source ID',
        field: 'source_id',
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'System',
        field: 'source_system',
        hide: true,
      },
      historyColumn,
    ]
  }, [
    apiColumnDefs,
    isMulti,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onCellValueChanged,
    onNestedCellValueChanged,
    props.mappingType,
  ])

  const statusFilterOptions = [
    { label: 'Mapped', value: 'mapped' },
    { label: 'Needs Mapping', value: 'needs mapping' },
    { label: 'Not Setup', value: 'unused' },
    { label: 'Not Found in Sync', value: 'not found in sync' },
  ] satisfies { label: string; value: MappingStatusFilter }[]

  const actionButtons = (
    <>
      <Select
        mode='multiple'
        value={statusFilters}
        onChange={setStatusFilters}
        options={statusFilterOptions}
        style={{ minWidth: '275px' }}
        placeholder='Filter by status'
        maxTagCount='responsive'
      />
      <MappingGridControls onSync={handleSync} onRefresh={refresh} />
    </>
  )

  const customSearchBar = (
    <Input
      style={{ width: '100%' }}
      placeholder='Search Mappings'
      onChange={(e) => setSearchTerm(e.target.value)}
    />
  )

  return (
    <>
      <GraviGrid<CrossroadsMapping, Record<string, unknown>>
        key={`reference-data-${props.mappingType}`}
        externalRef={gridApiRef}
        columnApiRef={columnApiRef}
        fullWidthSearchBar
        controlBarProps={{
          title: `${selectedTenant?.name} Reference Data`,
          actionButtons,
          customSearchBar,
          rowCount: totalRows,
        }}
        columnDefs={columnDefs}
        agPropOverrides={{
          defaultColDef: {
            resizable: true,
            filter: false,
            autoHeight: true,
            wrapText: true,
          },
          onFirstDataRendered: (params) => {
            params.columnApi?.autoSizeAllColumns()
          },
          domLayout: 'normal',
          animateRows: true,
          rowGroupPanelShow: 'never',
          getRowId: getRowId,
          suppressRowClickSelection: true,
          onRowClicked: onRowClicked,
          masterDetail: true,
          detailCellRendererParams: detailGridRendererParams,
          isRowMaster: (data) => data?.type === 'site',
          detailRowAutoHeight: true,
          pagination: true,
          paginationPageSize: 500,
          cacheBlockSize: 1000,
          maxBlocksInCache: 3,
          detailRowHeight: 250,
          rowModelType: 'serverSide',
          serverSideDatasource: datasource,
          isExternalFilterPresent: () => false,
          context: { selectedObject },
        }}
      />
      <HistoryDrawer
        open={!!historyMapping}
        onClose={() => setHistoryMapping(null)}
        history={mappingHistory}
        current={storedMapping}
        currentIsFullDocument
        loading={isHistoryFetching || isStoredMappingFetching}
        title={
          historyMapping
            ? `${historyMapping.display_name} History`
            : 'Mapping History'
        }
        emptyDescription='No changes recorded for this mapping'
        onRestore={canEditMappings ? handleRestore : undefined}
        restoringAuditId={restoringAuditId}
      />
    </>
  )
}
